import {
  Button,
  Form,
  Icons,
  Input,
  Popconfirm,
  Space,
  Table,
  TextField,
  useTable,
} from '@pankod/refine-antd'
import { useCustomMutation, useInvalidate } from '@pankod/refine-core'
import React, { useState } from 'react'

import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { ProgramTag } from '@resources/lms-management/tag/types'
import { TCommonError } from 'src/interfaces/common'

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean
  dataIndex: string
  record: ProgramTag
  index: number
  children: React.ReactNode
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  children,
  ...restProps
}) => {
  const inputNode = <Input autoComplete="off" />

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `${title} harus diisi.`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  )
}

const TagTableList = () => {
  const [form] = Form.useForm()
  const [editingKey, setEditingKey] = useState('')
  const invalidate = useInvalidate()
  const { tableProps } = useTable<ProgramTag, TCommonError, unknown>({
    resource: 'program-tags',
    dataProviderName: 'lms',
    syncWithLocation: true,
  })

  const { mutateAsync, isLoading } = useCustomMutation<
    {},
    TCommonError,
    Partial<ProgramTag>
  >()

  const isEditing = (record: ProgramTag) => record.id === editingKey

  const edit = (record: Partial<ProgramTag>) => {
    form.setFieldsValue({ ...record })
    setEditingKey(record.id!)
  }

  const cancel = () => {
    setEditingKey('')
  }

  const save = async () => {
    try {
      const row = (await form.validateFields()) as ProgramTag
      await mutateAsync({
        dataProviderName: 'lms',
        method: 'patch',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/program-tags/${editingKey}`,
        values: {
          name: row.name,
        },
        errorNotification: (err) =>
          showErrorNotification(err, 'Terdapat gangguan saat mengubah tag'),
        successNotification: {
          message: 'Tag berhasil diubah',
          type: 'success',
          description: 'Sukses',
        },
      })
      setEditingKey('')
    } finally {
      invalidate({
        resource: 'program-tags',
        dataProviderName: 'lms',
        invalidates: ['list'],
      })
    }
  }

  return (
    <Form form={form} component={false}>
      <Table
        {...tableProps}
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        rowKey={'id'}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: '10%',
          },
          {
            title: 'Kode',
            dataIndex: 'code',
            key: 'code',
            width: '20%',
            render: (text) => <TextField value={text} code />,
          },
          {
            title: 'Nama',
            dataIndex: 'name',
            key: 'name',
            width: '60%',
            onCell: (record: ProgramTag) => ({
              record,
              dataIndex: 'name',
              title: 'Nama',
              editing: isEditing(record),
            }),
          },
          {
            title: 'Aksi',
            dataIndex: 'actions',
            key: 'actions',
            width: '10%',
            render: (_, record) => {
              const editable = isEditing(record)
              if (editable) {
                return (
                  <Space direction="vertical" size="small">
                    <Button
                      icon={<Icons.SaveOutlined />}
                      type="primary"
                      onClick={save}
                      block
                      size="small"
                      loading={isLoading}
                    >
                      Simpan
                    </Button>
                    <Popconfirm
                      title="Apakah anda yakin membatalkan perubahan?"
                      onConfirm={cancel}
                      okText="Batalkan"
                      cancelText="Tidak"
                      placement="topRight"
                      disabled={isLoading}
                    >
                      <Button
                        icon={<Icons.CloseOutlined />}
                        danger
                        block
                        size="small"
                        disabled={isLoading}
                      >
                        Batal
                      </Button>
                    </Popconfirm>
                  </Space>
                )
              }
              return (
                <Space direction="vertical" size="small">
                  <Button
                    disabled={editingKey !== ''}
                    onClick={() => edit(record)}
                    icon={<Icons.EditOutlined />}
                    block
                  >
                    Ubah
                  </Button>
                </Space>
              )
            },
          },
        ]}
      />
    </Form>
  )
}

export default TagTableList
